import { ObjectTools } from '@sol/components/tools/object-tools';
import { PopoverTools } from '@sol/components/tools/popover-tools';
import { SolPopover, SolRectangle } from '@sol/components/services/popover.service';
import {
  ContentStyle,
  HostStyle,
  PopoverStyle,
  SolPopoverAlignment,
} from '@sol/components/components/popover/sol-popover/sol-popover.state';

export type LabPopoverPlacement = 'below' | 'above';
export interface LabPopoverStyle extends PopoverStyle {
  '--lab-popover-anchor-x'?: string;
}

const VIEWPORT_PADDING = 8;
const ANCHOR_MIN_OFFSET = 12;

/**
 * Lab popover position state.
 *
 * Strategy: always center the popover horizontally on the trigger; place above
 * or below based on the requested alignment, flipping to fit the viewport when
 * needed; slide horizontally (don't flip) when the centered position would
 * overflow the viewport.
 *
 * Width modes ride on top of centering:
 * - 'parent'      → popover width = trigger width (centered = edge-aligned)
 * - 'parent-grow' → min-width = trigger width, grows symmetrically
 * - 'auto'        → content-driven, centered on trigger
 */
export class LabPopoverState {
  private constructor(
    public readonly data: SolPopover<any> | undefined,
    public readonly hover: boolean,
    public readonly hostStyle: HostStyle,
    public readonly popoverStyle: LabPopoverStyle,
    public readonly contentStyle: ContentStyle,
    public readonly containerClass: string,
    public readonly popoverClass: string,
    public readonly popoverWidth: number,
    public readonly popoverHeight: number,
    public readonly clientWidth: number,
    public readonly clientHeight: number,
    public readonly fixed: boolean,
    public readonly popoverVisible: boolean,
  ) {}

  public static of(): LabPopoverState {
    return new LabPopoverState(undefined, false, {}, {}, {}, 'lab-popover-hidden', '', 0, 0, 0, 0, false, false);
  }

  public withData(data: SolPopover<any> | undefined): LabPopoverState {
    return this.copy({ data }).recalculate();
  }

  public withHover(hover: boolean): LabPopoverState {
    return this.copy({ hover }).recalculate();
  }

  public withResize(
    popoverWidth: number,
    popoverHeight: number,
    clientWidth: number,
    clientHeight: number,
  ): LabPopoverState {
    return this.copy({ popoverWidth, popoverHeight, clientWidth, clientHeight }).recalculate();
  }

  private recalculate(): LabPopoverState {
    const fixed = this.data?.options.bounds !== undefined;
    const popoverVisible = !!this.data;

    if (!this.data || this.popoverWidth === 0 || this.popoverHeight === 0) {
      return this.copy({
        containerClass: 'lab-popover-hidden',
        hostStyle: {},
        popoverClass: '',
        popoverStyle: {},
        fixed,
        popoverVisible,
      });
    }

    const clientWidth = this.clientWidth;
    const clientHeight = this.clientHeight;
    const parent: SolRectangle = PopoverTools.isValidParent(this.data.options.bounds)
      ? this.data.options.bounds
      : { x: 0, y: 0, width: 0, height: 0 };

    // Decide vertical placement: respect the consumer's requested side, flip
    // if it doesn't fit and the other side does.
    const wantAlign = this.data.options.align;
    const wantsBelow = wantAlign === SolPopoverAlignment.bottomLeft || wantAlign === SolPopoverAlignment.bottomRight;
    const gapY = this.data.options.gapY ?? 0;
    const fitsBelow = parent.y + parent.height + gapY + this.popoverHeight + VIEWPORT_PADDING <= clientHeight;
    const fitsAbove = parent.y - gapY - this.popoverHeight - VIEWPORT_PADDING >= 0;
    const placement: LabPopoverPlacement = wantsBelow
      ? fitsBelow || !fitsAbove
        ? 'below'
        : 'above'
      : fitsAbove || !fitsBelow
        ? 'above'
        : 'below';

    // Vertical position
    const hostY = placement === 'below' ? parent.y + parent.height + gapY : parent.y - gapY - this.popoverHeight;

    // Horizontal: anchor to point when the trigger is a point (0×0 bounds —
    // e.g. a context-menu pin to the cursor), otherwise center on trigger.
    // Slide to fit viewport in either case.
    const isPoint = parent.width === 0 && parent.height === 0;
    const desired = isPoint ? parent.x : parent.x + parent.width / 2 - this.popoverWidth / 2;
    const hostX = Math.max(VIEWPORT_PADDING, Math.min(desired, clientWidth - this.popoverWidth - VIEWPORT_PADDING));

    const hostStyle: HostStyle = {
      position: 'fixed',
      left: `${hostX}px`,
      top: `${hostY}px`,
    };

    // Container class — drives ring direction + animation origin
    const error = this.data.options.error;
    const containerErrorClass = error ? ' lab-popover-container-error' : '';
    const containerHoverClass = this.hover ? ' lab-popover-container-hidden' : '';
    const containerClass = `lab-popover-container-${placement}${containerErrorClass}${containerHoverClass}`;

    // Popover class — visible card styling + animation origin
    const errorClass = error ? ' lab-popover-error' : '';
    const fadeInClass = this.data.options.fadeIn ? ' lab-popover-fade-in' : '';
    const popoverClass = `lab-popover-${placement}${errorClass}${fadeInClass}`;

    // Width strategy
    const widthMode = this.data.options.width;
    // Expose the trigger center to anchored popover content. Normal popovers
    // ignore this; lab-context-dialog uses it to draw its own arrow without
    // giving every popover arrow chrome.
    const anchorX = parent.x + parent.width / 2;
    const anchorOffset = Math.max(ANCHOR_MIN_OFFSET, Math.min(anchorX - hostX, this.popoverWidth - ANCHOR_MIN_OFFSET));

    const popoverStyle: LabPopoverStyle = {
      minWidth: widthMode === 'parent' || widthMode === 'parent-grow' ? `${parent.width}px` : 'auto',
      maxWidth: widthMode === 'parent' ? `${parent.width}px` : 'auto',
      '--lab-popover-anchor-x': `${anchorOffset}px`,
    };

    // Cap the content height so it scrolls if it would overflow the viewport
    const maxHeight =
      placement === 'below' ? clientHeight - hostY - VIEWPORT_PADDING : parent.y - gapY - VIEWPORT_PADDING;
    const contentStyle: ContentStyle = {
      maxHeight: `${maxHeight}px`,
    };

    return this.copy({
      containerClass,
      hostStyle,
      popoverClass,
      popoverStyle,
      contentStyle,
      fixed,
      popoverVisible,
    });
  }

  private copy(props: Partial<LabPopoverState>): LabPopoverState {
    return ObjectTools.copy<LabPopoverState>(this, props);
  }
}
